import { create } from 'zustand';

import { Workspace, WorkspaceInput } from '../mainApi';
import { WorkspacesService } from '../services/workspaces.service';

type WorkspacesState = {
  workspaces: Workspace[];
  isLoadingWorkspaces: boolean;
  isSavingWorkspaces: boolean;
  workspacesError: string | null;
  loadWorkspaces: () => Promise<void>;
  addWorkspace: (input: WorkspaceInput) => Promise<void>;
  editWorkspace: (workspaceId: string, input: WorkspaceInput) => Promise<void>;
  removeWorkspace: (workspaceId: string, withSpaces: boolean) => Promise<void>;
};

const getWorkspaceId = (workspace: Workspace) => workspace.id ?? workspace._id ?? '';

const mapWorkspaces = (workspaces: Workspace[]) =>
  workspaces.map((workspace) => ({
    ...workspace,
    id: getWorkspaceId(workspace),
  }));

const withSave = async (
  set: (value: Partial<WorkspacesState>) => void,
  action: () => Promise<void>
) => {
  set({ isSavingWorkspaces: true, workspacesError: null });

  try {
    await action();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unable to save workspace changes.';
    set({ workspacesError: message });
    throw error;
  } finally {
    set({ isSavingWorkspaces: false });
  }
};

export const useWorkspacesStore = create<WorkspacesState>((set, get) => ({
  workspaces: [],
  isLoadingWorkspaces: true,
  isSavingWorkspaces: false,
  workspacesError: null,
  loadWorkspaces: async () => {
    set({ isLoadingWorkspaces: true, workspacesError: null });

    try {
      set({
        workspaces: mapWorkspaces(await WorkspacesService.getAll()),
        isLoadingWorkspaces: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load workspaces.';
      set({ workspacesError: message, isLoadingWorkspaces: false });
    }
  },
  addWorkspace: async (input) => {
    await withSave(set, async () => {
      await WorkspacesService.create(input);
      await get().loadWorkspaces();
    });
  },
  editWorkspace: async (workspaceId, input) => {
    await withSave(set, async () => {
      await WorkspacesService.update(workspaceId, input);
      await get().loadWorkspaces();
    });
  },
  removeWorkspace: async (workspaceId, withSpaces) => {
    await withSave(set, async () => {
      await WorkspacesService.remove(workspaceId, withSpaces);
      await get().loadWorkspaces();
    });
  },
}));
